import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BarChart3 } from 'lucide-react';

const data = [
  { month: 'Jan', conservative: 16, moderate: 20, aggressive: 30 },
  { month: 'Feb', conservative: 16.5, moderate: 21, aggressive: 31 },
  { month: 'Mar', conservative: 17, moderate: 22, aggressive: 32 },
  { month: 'Apr', conservative: 17.5, moderate: 23, aggressive: 33 },
  { month: 'May', conservative: 18, moderate: 24, aggressive: 34 },
  { month: 'Jun', conservative: 18.5, moderate: 25, aggressive: 35 },
  { month: 'Jul', conservative: 19, moderate: 26, aggressive: 36 },
  { month: 'Aug', conservative: 19.5, moderate: 27, aggressive: 37 },
  { month: 'Sep', conservative: 20, moderate: 28, aggressive: 38 },
  { month: 'Oct', conservative: 20, moderate: 29, aggressive: 39 },
  { month: 'Nov', conservative: 20, moderate: 29.5, aggressive: 39.5 },
  { month: 'Dec', conservative: 20, moderate: 30, aggressive: 40 },
];

const scenarioData = [
  { scenario: 'Conservative', monthlySales: '₹16L', investorShare: '₹1.6L', annualReturn: '₹19.2L' },
  { scenario: 'Moderate', monthlySales: '₹20L', investorShare: '₹2.0L', annualReturn: '₹24L' },
  { scenario: 'High Growth', monthlySales: '₹30L', investorShare: '₹3.0L', annualReturn: '₹36L' },
];

const RevenueCharts = () => {
  return (
    <section id="returns" className="py-24 md:py-32 bg-[#FDFBF7]">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#10B981]">Projected Returns</span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#022C22] mt-4 mb-4">
            Revenue Growth Projections
          </h2>
          <p className="text-base leading-relaxed text-[#4B5563] max-w-2xl mx-auto">
            12-month forecast based on market analysis and location performance
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white border border-[#E5E7EB] rounded-2xl shadow-lg p-8 md:p-12 mb-12"
        >
          <div className="flex items-center space-x-3 mb-8">
            <BarChart3 className="h-6 w-6 text-[#10B981]" />
            <h3 className="text-2xl font-semibold tracking-tight text-[#064E3B]">Monthly Sales Trajectory</h3>
          </div>
          
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorConservative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4B5563" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#4B5563" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorModerate" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorAggressive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#4B5563" />
              <YAxis stroke="#4B5563" label={{ value: 'Sales (₹L)', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontFamily: 'Manrope'
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="conservative" stroke="#4B5563" fillOpacity={1} fill="url(#colorConservative)" strokeWidth={2} />
              <Area type="monotone" dataKey="moderate" stroke="#10B981" fillOpacity={1} fill="url(#colorModerate)" strokeWidth={2} />
              <Area type="monotone" dataKey="aggressive" stroke="#F59E0B" fillOpacity={1} fill="url(#colorAggressive)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="overflow-x-auto"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-[#064E3B] mb-6">Return Scenarios</h3>
          <table className="w-full bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-sm">
            <thead className="bg-[#064E3B] text-white">
              <tr>
                <th className="text-left p-6 font-semibold">Scenario</th>
                <th className="text-left p-6 font-semibold">Monthly Sales</th>
                <th className="text-left p-6 font-semibold">Investor @ 10%</th>
                <th className="text-left p-6 font-semibold">Annual Return</th>
              </tr>
            </thead>
            <tbody>
              {scenarioData.map((row, index) => (
                <tr key={index} className="border-t border-[#E5E7EB] hover:bg-[#F3F0E6] transition-colors">
                  <td className="p-6 font-semibold text-[#022C22]">{row.scenario}</td>
                  <td className="p-6 data-font text-[#4B5563]">{row.monthlySales}</td>
                  <td className="p-6 data-font text-[#10B981] font-bold">{row.investorShare}</td>
                  <td className="p-6 data-font text-[#F59E0B] font-bold">{row.annualReturn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
};

export default RevenueCharts;
